interface Course {
  tytul: string;
  motorowodne?: boolean | number;
  dokimo?: string | number;
  poziom?: string;
  obraz_kursu?: string;
}

export const getImageUrl = (course: Course): string => {
  try {
    // Sprawdź czy kurs ma dane
    if (!course) {
      return '/images/course-default.webp';
    }

    // Sprawdź czy kurs ma bezpośrednio przypisany obraz
    if (course.obraz_kursu && course.obraz_kursu !== '') {
      return `/images/${course.obraz_kursu}`;
    }

    // Jeśli nie ma przypisanego obrazu, użyj domyślnego
    return '/images/course-default.webp';
  } catch (error) {
    console.error('Error in getImageUrl:', error);
    return '/images/course-default.webp';
  }
};

// Funkcja formatująca kod IMO - z obsługą przypadków specjalnych i usuwaniem końcowych zer
export const formatKodIMO = (kodimo: number | string | null | undefined): string => {
  if (kodimo === null || kodimo === undefined || kodimo === '') {
    return ''; // Zwróć pusty string dla nieprawidłowego wejścia
  }

  // Definicja specjalnych przypadków formatowania
  const specialCases: Record<string, string> = {
    '1.2826': '1.28/V.6',
    '1.2827': '1.28/V.7',
    '1.2928': '1.29/V.8',
    '1.2929': '1.29/V.9'
  };

  // Konwertuj kodimo na string, aby sprawdzić w specialCases
  const kodString = String(kodimo);

  // Sprawdź, czy kod znajduje się w specjalnych przypadkach
  if (specialCases[kodString]) {
    return specialCases[kodString];
  }

  // Sprawdź czy to przypadek kodu IMO z trzecią cyfrą równą 0 (np. 1.2000)
  const imoRegex = /^(\d+\.\d{2})0+$/;
  const regexMatch = kodString.match(imoRegex);
  if (regexMatch) {
    return regexMatch[1]; // Zwróć tylko część przed zerami (np. 1.20)
  }

  // Jeśli nie jest to przypadek specjalny, zastosuj ogólne formatowanie (usuwanie zer)
  try {
    // Konwertuj na Number, aby automatycznie usunąć końcowe zera i obsłużyć stringi
    const num = Number(kodimo);

    // Sprawdź, czy konwersja się powiodła (obsługa np. "abc")
    if (isNaN(num)) {
// Opcjonalnie zwróć oryginalny string lub pusty string/wskaźnik błędu
      return String(kodimo); // Zwróć oryginalną reprezentację string jako fallback
    }

    // Konwertuj liczbę z powrotem na string - to usunie nieznaczące zera po przecinku
    return num.toString();
  } catch (error) {
    console.error(`[formatKodIMO] Błąd formatowania kodimo "${kodimo}":`, error);
    return String(kodimo); // Zwróć oryginalną reprezentację string w razie błędu
  }
};

// Slug SEO z polskiego tytułu (transliteracja diakrytyków). Używany do URL-i typu
// /training/21-kurs-bezpieczenstwa-stcw-78-95 — słowa kluczowe w adresie poprawiają SEO/CTR.
const PL_MAP: Record<string, string> = {
  ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z',
};
export function slugify(text: string | null | undefined): string {
  return (text || '')
    .toLowerCase()
    .replace(/[ąćęłńóśźż]/g, (c) => PL_MAP[c] || c)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 70)
    .replace(/-+$/g, '');
}

// Buduje segment URL „{id}-{slug}". Route `[id]` i tak parsuje wiodące ID przez parseInt,
// więc działa bez kolumny slug w bazie (bare /training/21 nadal rozwiązuje się poprawnie).
export function courseSlug(id: number | string, tytul?: string | null): string {
  const s = slugify(tytul);
  return s ? `${id}-${s}` : String(id);
}
export function eventSlug(id: number | string, tytul?: string | null): string {
  const s = slugify(tytul);
  return s ? `${id}-${s}` : String(id);
}

/**
 * Próg widoczności terminów: pierwszy dzień bieżącego miesiąca (00:00 lokalnie).
 *
 * Reguła biznesowa: termin szkolenia pozostaje widoczny (harmonogram, kalendarz,
 * indeksacja SEO) przez CAŁY bieżący miesiąc. Minione terminy znikają DOPIERO po
 * wejściu kolejnego miesiąca — nie z dnia na dzień po dacie szkolenia.
 *
 * Odpowiednik w SQL: `DATE_FORMAT(CURDATE(), '%Y-%m-01')`.
 */
export function currentMonthStart(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
}

/**
 * Czy termin jest „miniony" w sensie widoczności — zakończył się przed bieżącym
 * miesiącem. Pod uwagę brana jest data zakończenia (a gdy jej brak — rozpoczęcia),
 * dzięki czemu kursy trwające na przełomie miesiąca nie znikają przedwcześnie.
 */
export function isTermPast(
  datarozpoczecia?: string | Date | null,
  datazakonczenia?: string | Date | null,
  now: Date = new Date()
): boolean {
  const ref = datazakonczenia || datarozpoczecia;
  if (!ref) return false;
  const end = new Date(ref);
  if (Number.isNaN(end.getTime())) return false;
  return end < currentMonthStart(now);
}
